import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import DeviceButtonTooltip from '../../placeholders/DeviceButtonTooltip';
import { Link as RouterLink } from 'react-router-dom';
import AdsClickOutlinedIcon from '@mui/icons-material/AdsClickOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import dialogStyles from './dialogStyles';
import { amber } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';

interface ConfirmableButtonProps {
  to: string;
  buttonText: string;
  buttonEndIcon?: boolean;
  color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'success' | 'info' | 'warning';
  variant?: 'text' | 'outlined' | 'contained';
  dialogTitle?: string;
  dialogMessage?: string;
  classicTooltip?: boolean;
  tooltipText?: React.ReactNode | string;
  isDisabled?: boolean;
  disabledReason?: string;
}

const ConfirmableButton: React.FC<ConfirmableButtonProps> = ({
  to,
  buttonText,
  buttonEndIcon = true,
  color = 'primary',
  variant = 'outlined',
  dialogTitle = 'Are you sure?',
  dialogMessage = 'Before interacting with the current device, please note that it is currently under maintenance.',
  classicTooltip = false,
  tooltipText = 'Connection to device',
  isDisabled = false,
  disabledReason = 'The current device is under HMS maintenance.',
}) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => setOpen(true);
  const handleCancel = () => setOpen(false);

  return (
    <>
      <DeviceButtonTooltip
        title={!isDisabled ? tooltipText : disabledReason}
        classicStyles={classicTooltip}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -9],
              },
            },
          ],
        }}
      >
        <Box component="span" sx={{ display: 'inline-block' }}>
          <Button
            variant={variant}
            color={color}
            endIcon={buttonEndIcon ? <AdsClickOutlinedIcon fontSize={'large'} /> : undefined}
            sx={{ marginLeft: 0.5, minWidth: 'auto' }}
            onClick={handleClick}
            disabled={isDisabled}
          >
            {buttonText}
          </Button>
        </Box>
      </DeviceButtonTooltip>

      <Dialog
        open={open}
        onClose={handleCancel}
        PaperProps={dialogStyles.dialog}
        sx={dialogStyles.dialogPaper}
        aria-labelledby="confirmable-dialog-title"
        aria-describedby="confirmable-dialog-content"
      >
        <DialogTitle
          id="confirmable-title"
          sx={{ ...dialogStyles.dialogTitle, backgroundColor: amber[800] }}
        >
          {dialogTitle}
          <IconButton aria-label="close" onClick={handleCancel} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent id="confirmable-content" sx={dialogStyles.dialogContent}>
          <DialogContentText sx={dialogStyles.dialogContentText}>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button onClick={handleCancel} sx={dialogStyles.cancelButtonStyles}>
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            component={RouterLink}
            to={to}
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmableButton;
